import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import axios from 'axios';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '@/routing/helpers/router';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import SiemensUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SiemensUserAccessCode';
import SiemensDiswUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SiemensDiswUserAccessCode';
import KorberUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/KorberUserAccessCode';
import SiemensHealthineersUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SiemensHealthineersUserAccessCode';
import SiemensInnomoticsUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SiemensInnomoticsUserAccessCode';
import VanderlandeUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/VanderlandeUserAccessCode';
import SikaUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SikaUserAccessCode';
import SmoUserAccessCode from '@/layout/screenSpecifics/UserAccessCode/SmoUserAccessCode';
import * as currentPageAction from '@/store/actions/currentPageAction';
import Translator from '@/layout/components/helpers/Translator';

export default function UserAccessCodeLoginScreen() {
  const { client } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState();
  const [message, setMessage] = useState();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showBtnCheckCode, setShowBtnCheckCode] = useState(true);
  const [showBtnDownloadDocument, setShowBtnDownloadDocument] = useState(false);
  const [showBtnSignUpCode, setShowBtnSignUpCode] = useState(false);
  const [loginValidationSchema] = useState(
    Yup.object().shape({
      username: Yup.string().required('Requerido'),
      password: Yup.string().required('Requerido'),
    }),
  );

  const handleCheckCode = async (values) =>
    await axios
      .post(axiosPath('api_r_open_userAccessCode_checkCode', true), { ...values })
      .then(async ({ data }) => {
        if (isMounted.current) {
          setMessage(data?.message);

          if (data.status === 'success') {
            setShowBtnCheckCode(false);
            setShowBtnDownloadDocument(true);
          } else if (data.status === 'isCreated') {
            setShowBtnCheckCode(true);
          } else if (data.status === 'isDownloaded') {
            setShowBtnCheckCode(false);
            setShowBtnSignUpCode(true);
          } else if (data.status === 'notFound') {
            setShowBtnCheckCode(true);
          }
        }
      })
      .catch((e) => dispatch(errorHandler(e)));

  const handleDownloadDocument = async (values) =>
    await axios
      .post(axiosPath('api_r_open_userAccessCode_downloadDocument', true), {
        ...values,
        client,
      })
      .then(async ({ data }) => {
        if (isMounted.current) {
          setMessage(data?.message);

          if (data.status === 'success' || data.status === 'isDownloaded') {
            setShowBtnDownloadDocument(false);
            setShowBtnSignUpCode(true);
            window.open(data.url, '_blank');
          } else if (data.status === 'isCreated' || data.status === 'notFound') {
            setShowBtnDownloadDocument(false);
            setShowBtnCheckCode(true);
          }
        }
      })
      .catch((e) => dispatch(errorHandler(e)));

  const handleSignUp = async (values) =>
    await axios
      .post(axiosPath('api_r_open_userAccessCode_signup', true), { ...values })
      .then(async ({ data }) => {
        if (isMounted.current) {
          setMessage(data?.message);

          if (data.status === 'success') {
            setIsSignUp(true);
          } else if (data.status === 'isCreated' || data.status === 'notFound') {
            setShowBtnSignUpCode(false);
            setShowBtnDownloadDocument(false);
            setShowBtnCheckCode(true);
          } else if (data.status === 'isNotDownloaded') {
            setShowBtnSignUpCode(false);
            setShowBtnDownloadDocument(true);
          } else if (data.status === 'notFound') {
            message.show();
            setShowBtnCheckCode(true);
          }

          setShowBtnSignUpCode(true);
          message.show();
        }
      })
      .catch((e) => dispatch(errorHandler(e)));

  const handleSubmission = async (values) => {
    if (showBtnCheckCode) {
      await handleCheckCode(values);
    } else if (showBtnDownloadDocument) {
      await handleDownloadDocument(values);
    } else if (showBtnSignUpCode) {
      await handleSignUp(values);
    }
  };

  const handleLogin = async (values) =>
    axios
      .post(axiosPath('api_r_user_login'), values)
      .then(async ({ data }) => {
        if (isMounted.current) {
          if (data['2fa_required']) {
            navigate(
              path({
                name: 'user_2fa_challenge',
                params: { method: data.method, challenge: data.challenge },
              }),
            );
          } else {
            await localStorage.setItem('t', data.token);
            await localStorage.setItem('rt', data.refresh_token);
            dispatch(currentPageAction.change('recentlyLoggedIn', true, true));
            navigate(
              path({
                name: 'user_home',
              }),
            );
          }
        }
      })
      .catch((e) => {
        if (e.response.status === 401) {
          setError(
            ReactDOMServer.renderToString(
              <Translator file="user" name="user-incorrect.login.details" />,
            ),
          );
        }
      });

  const getLogin = () => {
    switch (client) {
      case 'siemens':
        return (
          <SiemensUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'disw':
        return (
          <SiemensDiswUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'siemens-healthineers':
        return (
          <SiemensHealthineersUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'siemens-innomotics':
        return (
          <SiemensInnomoticsUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'vanderlande':
        return (
          <VanderlandeUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'sika':
        return (
          <SikaUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'smo':
        return (
          <SmoUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      case 'korber':
        return (
          <KorberUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
      default:
        return (
          <SiemensUserAccessCode
            loginValidationSchema={loginValidationSchema}
            showBtnCheckCode={showBtnCheckCode}
            showBtnDownloadDocument={showBtnDownloadDocument}
            showBtnSignUpCode={showBtnSignUpCode}
            isSignUp={isSignUp}
            message={message}
            error={error}
            onHandleSubmission={handleSubmission}
            onHandleLogin={handleLogin}
          />
        );
    }
  };

  return <Container>{getLogin()}</Container>;
}

const Container = styled.div`
  padding-top: 30px;
  .user-access-code-login {
    @media (min-width: 768px) {
      width: 600px;
    }
    margin: 0 auto 0 auto;
    font-size: 14px;
    .btn-login {
      border-radius: 3px;
      margin-top: 20px;
      background-color: #003d47;
      &:disabled {
        opacity: 0.5;
        &:hover {
          cursor: not-allowed;
        }
      }
    }
    .form-control {
      font-size: 14px;
      margin: 6px 0;
      background: rgba(238, 238, 238, 0.37);
      border: none;
      padding: 20px 10px;
      @media all and (-ms-high-contrast: none) {
        padding: 5px 10px;
      }
      &::placeholder,
      ::-webkit-input-placeholder {
        color: #003d47;
        opacity: 0.5;
        font-weight: normal;
      }
      &:focus {
        color: #003d47;

        box-shadow: none;
      }
    }
    img {
      display: block;
      margin: 0 auto 0 auto;
      width: 300px;
    }
    h1 {
      color: #00a6b5;
      text-align: center;
      margin: 0 0 40px 0;
      font-size: 18px;
    }
    .sika-title {
      text-align: center;
      margin: 40px 0;
      font-size: 18px;
    }
    label {
      font-weight: bold;
      color: #003d47;
    }
    .signup-block {
      @media (min-width: 768px) {
        border-right: 1px solid #ccc;
      }
      .signup-wrapper {
        > label {
          margin-bottom: 8px;
        }
      }
      .signup-success-wrapper {
        display: flex;
        height: 180px;
        color: #23923d;
        background: #c3f4ce;
        border: 1px solid #23923d;
        border-radius: 4px;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        flex-direction: column;
      }
    }
  }
`;
